/**
 * lib/kartoteki/kartoteka-preview-fields.ts — mapowanie pól kartoteki systemowej
 * (`KartotekaBuilderField`, słownik typów SYSTEM_EXT_FIELD_TYPES + typy rdzeniowe)
 * na pola buildera (`ModuleTemplateFieldDefinition`), którymi żywi się podgląd
 * „na żywo" pozycji menu (generator próbek + kolumny listy + sekcje formularza).
 *
 * Użycie: zunifikowany podgląd menu dla encji-odwołań (`reference_to_system_entity`)
 * — pola kartoteki przychodzą z GET /admin/kartoteki/{slug}/definition, a podgląd
 * renderuje je tą samą ścieżką co pola encji `new_table`.
 *
 * Ograniczenie (świadome): endpoint definicji nie zwraca opcji pól select/multiselect,
 * więc próbki tych pól są puste (podgląd pokazuje pustą komórkę, nie zmyślone opcje).
 */

import type { ModuleTemplateFieldDefinition, ModuleTemplateFieldType } from '@saas/shared';

import type { KartotekaBuilderField } from './kartoteka-definition';

/** Typ kartoteki → typ pola buildera; nieznane typy zachowawczo jako `text`. */
export function mapKartotekaFieldType(fieldType: string): ModuleTemplateFieldType {
  switch (fieldType) {
    case 'text':
    case 'longtext':
      return 'longtext';
    case 'varchar':
    case 'email':
    case 'url':
      return 'text';
    case 'integer':
    case 'number':
      return 'number';
    case 'numeric':
    case 'decimal':
      return 'decimal';
    case 'boolean':
      return 'boolean';
    case 'date':
      return 'date';
    case 'timestamp':
    case 'datetime':
      return 'datetime';
    case 'select':
      return 'select';
    case 'multiselect':
    case 'multi_select':
      return 'multi_select';
    case 'reference':
      return 'reference';
    default:
      return 'text';
  }
}

/**
 * Pola kartoteki → pola buildera dla podglądu (sort po sortOrder, stabilnie po slugu).
 */
export function mapKartotekaFieldsForPreview(
  fields: KartotekaBuilderField[],
): ModuleTemplateFieldDefinition[] {
  return fields
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.slug.localeCompare(b.slug))
    .map((f) => ({
      slug: f.slug,
      name: f.name,
      type: mapKartotekaFieldType(f.fieldType),
      isRequired: f.isRequired,
      sortOrder: f.sortOrder,
    }));
}
